import { useState, useMemo } from 'react';

interface UseProfileSectionsProps<T extends {
  id: string;
  title: string;
  authors?: string[];
  author?: string;
  rating_star?: number;
  review_text?: string;
  read_at?: string;
  finished_at?: string;
  created_at?: string;
  shelf?: string;
  tags?: string[];
  [key: string]: any;
}> {
  books: T[];
}

export function useProfileSections<T extends {
  id: string;
  title: string;
  authors?: string[];
  author?: string;
  rating_star?: number;
  review_text?: string;
  read_at?: string;
  finished_at?: string;
  created_at?: string;
  shelf?: string;
  tags?: string[];
  [key: string]: any;
}>({ books }: UseProfileSectionsProps<T>) {
  // Section expansion state
  const [sectionsExpanded, setSectionsExpanded] = useState({
    currentlyReading: true, // Always show currently reading
    paused: false,
    reviews: false,
    tbr: false,
    dnf: false,
  });

  // Review search and filter state
  const [reviewsSearch, setReviewsSearch] = useState('');
  const [reviewsRatingFilter, setReviewsRatingFilter] = useState<number | null>(null);
  const [reviewsSortBy, setReviewsSortBy] = useState<'recent' | 'highest' | 'lowest'>('recent');

  // TBR category filter state
  const [tbrCategoryFilter, setTbrCategoryFilter] = useState<string | null>(null);

  // Tag filter state for each section
  const [tbrTagFilter, setTbrTagFilter] = useState<string[]>([]);
  const [reviewsTagFilter, setReviewsTagFilter] = useState<string[]>([]);
  const [pausedTagFilter, setPausedTagFilter] = useState<string[]>([]);
  const [dnfTagFilter, setDnfTagFilter] = useState<string[]>([]);

  // Display limits for collapsed sections
  const [displayLimits] = useState({
    reviews: 5,
    tbr: 10,
    dnf: 5,
  });

  // Toggle section expansion
  const toggleSection = (section: keyof typeof sectionsExpanded) => {
    setSectionsExpanded(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Get books by shelf
  const currentlyReadingBooks = useMemo(
    () => books.filter(book => book.shelf === 'reading'),
    [books]
  );

  const pausedBooks = useMemo(() => {
    let filtered = books.filter(book => book.shelf === 'paused');
    
    // Apply tag filter
    if (pausedTagFilter.length > 0) {
      filtered = filtered.filter(book => 
        book.tags?.some(tag => pausedTagFilter.includes(tag))
      );
    }
    
    return filtered;
  }, [books, pausedTagFilter]);

  // Compute available tags for paused books
  const pausedAvailableTags = useMemo(() => {
    const allTags = books
      .filter(book => book.shelf === 'paused')
      .flatMap(book => book.tags || []);
    return [...new Set(allTags)].sort();
  }, [books]);

  const tbrBooks = useMemo(() => {
    let filtered = books.filter(book => book.shelf === 'to-read');
    
    // Apply category filter
    if (tbrCategoryFilter) {
      filtered = filtered.filter(book => book.tbr_category === tbrCategoryFilter);
    }
    
    // Apply tag filter
    if (tbrTagFilter.length > 0) {
      filtered = filtered.filter(book => 
        book.tags?.some(tag => tbrTagFilter.includes(tag))
      );
    }
    
    return filtered;
  }, [books, tbrCategoryFilter, tbrTagFilter]);

  // Compute available tags for TBR books
  const tbrAvailableTags = useMemo(() => {
    const allTags = books
      .filter(book => book.shelf === 'to-read')
      .flatMap(book => book.tags || []);
    return [...new Set(allTags)].sort();
  }, [books]);

  const dnfBooks = useMemo(() => {
    let filtered = books.filter(book => book.shelf === 'unfinished');
    
    // Apply tag filter
    if (dnfTagFilter.length > 0) {
      filtered = filtered.filter(book => 
        book.tags?.some(tag => dnfTagFilter.includes(tag))
      );
    }
    
    return filtered;
  }, [books, dnfTagFilter]);

  // Compute available tags for DNF books
  const dnfAvailableTags = useMemo(() => {
    const allTags = books
      .filter(book => book.shelf === 'unfinished')
      .flatMap(book => book.tags || []);
    return [...new Set(allTags)].sort();
  }, [books]);

  // Get all reviews (unfiltered) for count
  const allReviewBooks = useMemo(
    () => books.filter(book => (book.review_text || book.rating_star) && book.shelf === 'read'),
    [books]
  );

  // Compute available tags for reviewed books
  const reviewsAvailableTags = useMemo(() => {
    const allTags = allReviewBooks.flatMap(book => book.tags || []);
    return [...new Set(allTags)].sort();
  }, [allReviewBooks]);

  // Filter and sort reviews
  const reviewBooks = useMemo(() => {
    let filtered = allReviewBooks;

    // Apply search filter
    if (reviewsSearch) {
      const search = reviewsSearch.toLowerCase();
      filtered = filtered.filter(
        book =>
          book.title.toLowerCase().includes(search) ||
          book.authors?.some(author => author.toLowerCase().includes(search)) ||
          book.author?.toLowerCase().includes(search) ||
          book.review_text?.toLowerCase().includes(search)
      );
    }

    // Apply rating filter
    if (reviewsRatingFilter !== null) {
      filtered = filtered.filter(book => book.rating_star === reviewsRatingFilter);
    }

    // Apply tag filter
    if (reviewsTagFilter.length > 0) {
      filtered = filtered.filter(book => 
        book.tags?.some(tag => reviewsTagFilter.includes(tag))
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      if (reviewsSortBy === 'recent') {
        const dateA = new Date(a.read_at || a.finished_at || a.created_at || 0).getTime();
        const dateB = new Date(b.read_at || b.finished_at || b.created_at || 0).getTime();
        return dateB - dateA;
      } else if (reviewsSortBy === 'highest') {
        return (b.rating_star || 0) - (a.rating_star || 0);
      } else {
        return (a.rating_star || 0) - (b.rating_star || 0);
      }
    });

    return filtered;
  }, [allReviewBooks, reviewsSearch, reviewsRatingFilter, reviewsSortBy, reviewsTagFilter]);

  // Get limited versions for collapsed state
  const displayedReviews = sectionsExpanded.reviews
    ? reviewBooks
    : reviewBooks.slice(0, displayLimits.reviews);

  const displayedTbr = sectionsExpanded.tbr
    ? tbrBooks
    : tbrBooks.slice(0, displayLimits.tbr);

  const displayedDnf = sectionsExpanded.dnf
    ? dnfBooks
    : dnfBooks.slice(0, displayLimits.dnf);

  return {
    // Section state
    sectionsExpanded,
    toggleSection,

    // Review search/filter state
    reviewsSearch,
    setReviewsSearch,
    reviewsRatingFilter,
    setReviewsRatingFilter,
    reviewsSortBy,
    setReviewsSortBy,

    // TBR category filter state
    tbrCategoryFilter,
    setTbrCategoryFilter,

    // Tag filter state
    tbrTagFilter,
    setTbrTagFilter,
    reviewsTagFilter,
    setReviewsTagFilter,
    pausedTagFilter,
    setPausedTagFilter,
    dnfTagFilter,
    setDnfTagFilter,

    // Available tags for each shelf
    tbrAvailableTags,
    reviewsAvailableTags,
    pausedAvailableTags,
    dnfAvailableTags,

    // Book lists
    currentlyReadingBooks,
    pausedBooks,
    allReviewBooks,
    reviewBooks,
    tbrBooks,
    dnfBooks,

    // Displayed (potentially limited) lists
    displayedReviews,
    displayedTbr,
    displayedDnf,

    // Display limits
    displayLimits,
  };
}
